import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { remoteBankingContactsState } from './initialState';
import {
  contactInformationFormFulfilled,
  contactInformationFormPending,
} from '~/store/remote-banking-contacts/actions/contactInformationActions.ts';
import {
  remoteBankingContactsError,
  remoteBankingContactsFulfilled,
  remoteBankingContactsPending,
  verifyContrAgentError,
} from '~/store/remote-banking-contacts/actions/actions.ts';
import { contactInformationFormForm } from '~/store/remote-banking-contacts/thunks/contactInformationThunks.ts';
import {
  remoteBankingContactsManager,
  remoteBankingContactsManagerForm,
} from '~/store/remote-banking-contacts/thunks/managerFormThunks.ts';
import { validateDocumentRemoteBankingContacts } from '~/store/remote-banking-contacts/thunks/validateDocumentRemoteBankingThunks.ts';
import {
  validateDocumentFulfilled,
  validateDocumentPending,
} from '~/store/remote-banking-contacts/actions/validateDocumentRemoteBankingActions.ts';
import {
  responsiblePersons,
  responsiblePersonsAccountManagers,
  responsiblePersonsInformation,
} from '~/store/remote-banking-contacts/thunks/responsiblePersonsThunks.ts';
import {
  responsiblePersonsFormFulfilled,
  responsiblePersonsFulfilled,
  responsiblePersonsInfoFormFulfilled,
  responsiblePersonsPending,
} from '~/store/remote-banking-contacts/actions/responsiblePersonsActions.ts';
import {
  remoteBankContactCurrentContactDetailsInfo,
  remoteBankContactResponsiblePersonsForm,
} from '~/store/remote-banking-contacts/thunks/responsiblePersonsFormThunks.ts';
import {
  statusVerifyContrAgent,
  verifyContrAgentRemoteBankingContacts,
} from '~/store/remote-banking-contacts/thunks/verifyContragentThunks.ts';
import { processRepresentatives } from '~/store/remote-banking-contacts/thunks/processRepresentativesThunks.ts';

const remoteBankingContactsSlice = createSlice({
  name: 'remoteBankingContacts',
  initialState: remoteBankingContactsState,
  reducers: {
    clearRemoteBankingContactsState() {
      return remoteBankingContactsState;
    },
    setRemoteBankingContactsError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(contactInformationFormForm.fulfilled, contactInformationFormFulfilled)
      .addCase(contactInformationFormForm.pending, contactInformationFormPending)
      .addCase(contactInformationFormForm.rejected, remoteBankingContactsError)

      .addCase(remoteBankingContactsManagerForm.fulfilled, remoteBankingContactsFulfilled)
      .addCase(remoteBankingContactsManagerForm.pending, remoteBankingContactsPending)
      .addCase(remoteBankingContactsManagerForm.rejected, remoteBankingContactsError)

      .addCase(remoteBankingContactsManager.fulfilled, remoteBankingContactsFulfilled)
      .addCase(remoteBankingContactsManager.pending, remoteBankingContactsPending)
      .addCase(remoteBankingContactsManager.rejected, remoteBankingContactsError)

      .addCase(validateDocumentRemoteBankingContacts.fulfilled, validateDocumentFulfilled)
      .addCase(validateDocumentRemoteBankingContacts.pending, validateDocumentPending)
      .addCase(validateDocumentRemoteBankingContacts.rejected, remoteBankingContactsError)

      .addCase(responsiblePersonsAccountManagers.fulfilled, responsiblePersonsFulfilled)
      .addCase(responsiblePersonsAccountManagers.pending, responsiblePersonsPending)
      .addCase(responsiblePersonsAccountManagers.rejected, remoteBankingContactsError)

      .addCase(responsiblePersons.fulfilled, responsiblePersonsFormFulfilled)
      .addCase(responsiblePersons.pending, responsiblePersonsPending)
      .addCase(responsiblePersons.rejected, remoteBankingContactsError)

      .addCase(responsiblePersonsInformation.fulfilled, responsiblePersonsInfoFormFulfilled)
      .addCase(responsiblePersonsInformation.pending, responsiblePersonsPending)
      .addCase(responsiblePersonsInformation.rejected, remoteBankingContactsError)

      .addCase(remoteBankContactResponsiblePersonsForm.fulfilled, remoteBankingContactsFulfilled)
      .addCase(remoteBankContactResponsiblePersonsForm.pending, remoteBankingContactsPending)
      .addCase(remoteBankContactResponsiblePersonsForm.rejected, remoteBankingContactsError)

      .addCase(remoteBankContactCurrentContactDetailsInfo.fulfilled, remoteBankingContactsFulfilled)
      .addCase(remoteBankContactCurrentContactDetailsInfo.pending, remoteBankingContactsPending)
      .addCase(remoteBankContactCurrentContactDetailsInfo.rejected, remoteBankingContactsError)

      .addCase(verifyContrAgentRemoteBankingContacts.fulfilled, remoteBankingContactsFulfilled)
      .addCase(verifyContrAgentRemoteBankingContacts.pending, remoteBankingContactsPending)
      .addCase(verifyContrAgentRemoteBankingContacts.rejected, verifyContrAgentError)

      .addCase(processRepresentatives.fulfilled, remoteBankingContactsFulfilled)
      .addCase(processRepresentatives.pending, remoteBankingContactsPending)
      .addCase(processRepresentatives.rejected, remoteBankingContactsError)

      .addCase(statusVerifyContrAgent.fulfilled, remoteBankingContactsFulfilled)
      .addCase(statusVerifyContrAgent.pending, remoteBankingContactsPending)
      .addCase(statusVerifyContrAgent.rejected, remoteBankingContactsError);
  },
});

export const { clearRemoteBankingContactsState, setRemoteBankingContactsError } =
  remoteBankingContactsSlice.actions;

export default remoteBankingContactsSlice.reducer;
